import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Percent } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures, useStable } from '~/hooks';
import { colors, weights } from '~/styles';
import { QueryString } from '~/utils';

const Container = styled.div`
  flex: 1;
  display: flex;
  margin: 0.25rem;
  background-color: ${colors.white};
  border-radius: 0.625rem;
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
`;

const Summary = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.5rem;
`;

const Title = styled.div`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  color: ${colors.primary};
  font-size: 2.25rem;
  font-weight: ${weights.light};
`;

const SummaryLabel = styled.div`
  margin-top: 0.25rem;
  color: ${colors.grey25};
  font-size: 0.625rem;
  text-align: center;
  text-transform: uppercase;
`;

const SummaryInfo = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;

  &:hover ${SummaryValue} {
    color: ${colors.accent};
  }
`;

const Breakdowns = styled.div`
  display: flex;
  flex-direction: column;
`;

const BreakdownLabel = styled.div`
  color: ${colors.grey40};
  font-size: 0.625rem;
`;

const BreakdownValue = styled.div`
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
`;

const BreakdownItem = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  padding-right: 2rem;
  border: solid 0.125rem ${colors.grey5};
  border-right: none;
  border-bottom: none;

  &:first-child {
    border-top: none;
  }

  &:hover ${BreakdownValue} {
    color: ${colors.grey55};
  }
`;

export default function Utilization({ entries, startDate, endDate, member: selectedMember }) {
  const features = useFeatures();
  const { workspace } = useWorkspace();

  const member = useMemo(() => {
    return selectedMember ?? workspace.member;
  }, [selectedMember, workspace]);

  const api = useApi();
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    if (!features.utilization) return;

    try {
      const { data } = await api.www.workspaces(workspace.id).timeEntries().getUtilizationSummary({
        start: startDate,
        end: endDate,
        memberId: member?.id,
      });

      setData(data);
    } catch (error) {
      setData(null);
    }
  }, [features.utilization, api, workspace.id, startDate, endDate, member]);

  const stableEntries = useStable(entries);
  useEffect(() => {
    fetchData();
  }, [fetchData, stableEntries]);

  const utilizationByMember = (query = {}) =>
    `/app/${workspace.key}/reports/utilization-by-member${new QueryString(
      {
        start: startDate,
        end: endDate,
        memberId: member?.id,
        memberBillableTypeId: 'all',
        memberEmploymentTypeId: 'all',
        ...query,
      },
      { multi: true },
    ).toString(true)}`;

  if (!features.utilization) return null;
  if (!data) return <Container />;

  const { billableUtilization, targetBillable, attainment } = data;

  return (
    <Container>
      <Summary>
        <Title>Utilization</Title>
        <SummaryInfo to={utilizationByMember()}>
          <SummaryValue>
            <Percent value={billableUtilization} minimumFractionDigits={1} maximumFractionDigits={1} />
          </SummaryValue>
          <SummaryLabel>Billable Utilization</SummaryLabel>
        </SummaryInfo>
      </Summary>
      <Breakdowns>
        <BreakdownItem to={utilizationByMember()}>
          <BreakdownLabel>Target Billable</BreakdownLabel>
          <BreakdownValue>
            <Percent value={targetBillable} minimumFractionDigits={1} maximumFractionDigits={1} />
          </BreakdownValue>
        </BreakdownItem>
        <BreakdownItem to={utilizationByMember()}>
          <BreakdownLabel>Attainment</BreakdownLabel>
          <BreakdownValue>
            <Percent value={attainment} minimumFractionDigits={1} maximumFractionDigits={1} />
          </BreakdownValue>
        </BreakdownItem>
      </Breakdowns>
    </Container>
  );
}
